<script setup>
import { useMutation } from "@tanstack/vue-query";
import Dialog from "primevue/dialog";
import { computed, ref, toRaw, toRefs, watch } from "vue";
import { useToast } from "vue-toast-notification";
import MeetingPointInfo from "@/components/MeetingPointInfo";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "@/constants";
import ordersService from "@/services/orders";
import { MEETING_POINT_KEYS } from "./constants";
import { dialogPt } from "./style";
import { hasMeetingPointData } from "./utils";

defineOptions({
  name: "MeetingPointModal",
});

const props = defineProps({
  visible: {
    type: Boolean,
    default: true,
  },
  order: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(["update:visible", "onUpdate"]);

const toast = useToast();
const { visible } = toRefs(props);
const orderUpdatedData = ref(undefined);

watch(
  () => props.order,
  (newVal) => {
    if (newVal?.order) {
      orderUpdatedData.value = Object.fromEntries(MEETING_POINT_KEYS.map((key) => [key, newVal.order?.[key]]));
      orderUpdatedData.value.meeting_point_city = newVal.order?.meeting_point_city;
    }
  },
  { immediate: true },
);

const prefixedPayload = computed(() =>
  Object.fromEntries(Object.entries(orderUpdatedData.value).map(([key, value]) => [`order_${key}`, value])),
);
const payload = computed(() => ({
  ...toRaw(props.order?.order ?? {}),
  ...prefixedPayload.value,
}));
const { mutate: updateOrder, isPending: isUpdateOrderPending } = useMutation({
  mutationKey: ["updateOrder"],
  mutationFn: () =>
    ordersService.updateOrder({
      order_id: props.order?.order?.id,
      payload: payload.value,
      callingLocation: "MeetingPointModal",
    }),
  onSuccess: (data) => {
    if (data?.data) {
      toast.open({
        message: "Order details updated successfully.",
        type: "success",
        position: "bottom",
        duration: SUCCESS_TIMEOUT,
      });
    } else {
      toast.open({
        message: "Failed to save Order: Order information is missing.",
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    }
    emit("onUpdate", data?.data);
  },
  onError: (error) => {
    toast.open({
      message: `Failed to save Order: ${error?.message}.`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});

const enabledSave = computed(() => hasMeetingPointData(orderUpdatedData.value));

const handleMeetingPointChange = (updatedData) => {
  orderUpdatedData.value = updatedData;
};
const submit = () => {
  updateOrder();
};
const handleCloseModal = () => {
  emit("update:visible", false);
};
</script>
<template>
  <Dialog :visible="visible" modal header="Edit Meeting Point" :pt="dialogPt">
    <template #closeicon>
      <button class="flex h-6 w-6 items-center justify-center text-teal-900" @click="handleCloseModal">
        <CloseIcon size="28" stroke-width="1.75" />
      </button>
    </template>
    <div class="text-gray-700">
      <MeetingPointInfo :meeting-point-location="orderUpdatedData" @update="handleMeetingPointChange" />
      <div class="flex justify-center">
        <BaseButton outlined :disabled="isUpdateOrderPending" :on-click="handleCloseModal">Cancel</BaseButton>
        <BaseButton :disabled="isUpdateOrderPending || !enabledSave" :on-click="submit">Save</BaseButton>
      </div>
    </div>
  </Dialog>
</template>
